import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback } from 'react';
import { ArrayInput, Form, FormDataConsumer, RemoveItemButton, SimpleFormIterator, TextInput, useListContext, useTranslate, } from 'react-admin';
import { styled } from '@mui/material';
import clsx from 'clsx';
import { getListFiltersFromFormValues } from './getListFiltersFromFormValues';
import { getFormValuesFromListFilters } from './getFormValuesFromListFilters';
import { StackedFiltersFormActions } from './StackedFiltersActions';
import { SourceInput } from './SourceInput';
import { OperatorInput } from './OperatorInput';
/**
 * An alternative to the <Filter> component that add the concept of operator.
 * It allows users to apply an operator with a value to multiple fields.
 *
 * @example
 * import { List, NumberInput } from 'react-admin';
 * import { StackedFilters, FiltersConfig, textFilter, numberFilter, referenceFilter, booleanFilter } from '@react-admin/ra-form-layout';
 * import { MyNumberRangeInput } from './MyNumberRangeInput';
 *
 * const PostListFilters: FiltersConfig = {
 *     title: textFilter(),
 *     views: numberFilter(),
 *     tags: referenceFilter({ reference: 'tags' }),
 *     published: booleanFilter(),
 *     note: {
 *         operators: [
 *            { value: 'eq', label: 'Equals' },
 *            { value: 'neq', label: 'Not Equals' },
 *            { value: 'between', label: 'Between', input: ({ source }) => <MyNumberRangeInput source={source} /> },
 *         ],
 *         input: ({ source }) => <NumberInput source={source} />,
 *     }
 * };
 * const PostList = (props) => (
 *    <ListBase {...props}>
 *       <Accordion>
 *           <AccordionSummary
 *               expandIcon={<ExpandMoreIcon />}
 *               aria-controls="filters-panel-content"
 *               id="filters-panel-header"
 *           >
 *               <Typography>Filters</Typography>
 *           </AccordionSummary>
 *           <AccordionDetails>
 *               <StackedFiltersForm config={PostListFilters} />
 *           </AccordionDetails>
 *       </Accordion>
 *       ...
 *     </ListBase>
 * );
 * @param props
 * @param props.config {FilterConfig} The filters configuration.
 * @param props.onFiltersApplied Callback function called after the filters have been applied.
 * @returns A filter form for a <List>.
 */
export var StackedFiltersForm = function (props) {
    var className = props.className, config = props.config, onFiltersApplied = props.onFiltersApplied, sx = props.sx;
    var translate = useTranslate();
    var _a = useListContext(), filterValues = _a.filterValues, setFilters = _a.setFilters;
    var onApplyFilters = useCallback(function (values) {
        var filters = values.filters;
        var newFilters = getListFiltersFromFormValues(filters);
        setFilters(newFilters, Object.keys(newFilters).reduce(function (acc, key) {
            acc[key] = true;
            return acc;
        }, {}));
        if (onFiltersApplied && typeof onFiltersApplied === 'function') {
            onFiltersApplied();
        }
    }, [onFiltersApplied, setFilters]);
    var appliedFilters = {
        filters: getFormValuesFromListFilters(filterValues, config),
    };
    var sourceChoices = Object.keys(config).map(function (source) { return ({
        id: source,
        name: source,
        label: config[source].label,
    }); });
    return (_jsx(Root, { className: clsx(StackedFiltersFormClasses.root, className), onSubmit: onApplyFilters, defaultValues: appliedFilters, sx: sx, children: _jsx(ArrayInput, { label: false, source: "filters", children: _jsxs(SimpleFormIterator, { inline: true, disableReordering: true, disableClear: true, addButton: _jsx(StackedFiltersFormActions, { onFiltersApplied: onFiltersApplied }), removeButton: _jsx(RemoveItemButton, {}), sx: {
                    '& .RaSimpleFormIterator-inline': {
                        width: '100%',
                        alignItems: 'center',
                    },
                    '& .RaSimpleFormIterator-action': {
                        display: 'flex',
                        alignItems: 'center',
                    },
                    '& .RaSimpleFormIterator-add': {
                        marginTop: 1,
                        width: '100%',
                    },
                }, children: [_jsx(SourceInput, { className: StackedFiltersFormClasses.sourceInput, source: "source", label: translate('ra-form-layout.filters.source', {
                            _: 'Source',
                        }), choices: sourceChoices }), _jsx(FormDataConsumer, { children: function (_a) {
                            var _b;
                            var scopedFormData = _a.scopedFormData;
                            if (!scopedFormData)
                                return null;
                            var source = scopedFormData.source;
                            var operators = ((_b = config[source]) !== null && _b !== void 0 ? _b : {
                                operators: [],
                            }).operators;
                            return (_jsx(OperatorInput, { className: StackedFiltersFormClasses.operatorInput, source: "operator", operators: operators, label: translate('ra-form-layout.filters.operator', {
                                    _: 'Operator',
                                }), valueInputSource: "value", config: config }));
                        } }), _jsx(FormDataConsumer, { children: function (_a) {
                            var _b;
                            var scopedFormData = _a.scopedFormData;
                            if (!scopedFormData)
                                return null;
                            var source = scopedFormData.source;
                            var operator = scopedFormData.operator;
                            var _c = (_b = config[source]) !== null && _b !== void 0 ? _b : {
                                operators: [],
                                // eslint-disable-next-line react/display-name
                                input: function (_a) {
                                    var source = _a.source;
                                    return (_jsx(TextInput, { className: StackedFiltersFormClasses.valueInput, label: translate('ra-form-layout.filters.value', {
                                            _: 'Value',
                                        }), source: source, disabled: true, helperText: false }));
                                },
                            }, operators = _c.operators, input = _c.input;
                            var operatorConfig = operators.find(function (o) { return o.value === operator; });
                            return operatorConfig && operatorConfig.input
                                ? operatorConfig.input({
                                    className: StackedFiltersFormClasses.valueInput,
                                    operator: operator,
                                    source: 'value',
                                    label: translate('ra-form-layout.filters.value', {
                                        _: 'Value',
                                    }),
                                })
                                : input
                                    ? input({
                                        className: StackedFiltersFormClasses.valueInput,
                                        operator: operator,
                                        source: 'value',
                                        label: translate('ra-form-layout.filters.value', {
                                            _: 'Value',
                                        }),
                                    })
                                    : null;
                        } })] }) }) }));
};
var PREFIX = 'RaStackedFiltersForm';
export var StackedFiltersFormClasses = {
    root: "".concat(PREFIX, "-root"),
    sourceInput: "".concat(PREFIX, "-sourceInput"),
    operatorInput: "".concat(PREFIX, "-operatorInput"),
    valueInput: "".concat(PREFIX, "-valueInput"),
};
var Root = styled(Form, {
    name: PREFIX,
    overridesResolver: function (props, styles) { return styles.root; },
})(function () {
    var _a;
    return (_a = {},
        _a["* .".concat(StackedFiltersFormClasses.valueInput)] = {
            justifyContent: 'center',
        },
        _a);
});
